<template>
    <div>
        <b-row>
            <b-col md="6">
                <b-form-group label="Navigasyon Anahtarı" label-for="navigationKey" class="is-required">
                    <b-form-input id="navigationKey" v-model="localValue.navigationKey" placeholder="services, about, contact" :state="getValidationState('navigationKey')" />
                    <b-form-invalid-feedback :state="getValidationState('navigationKey')">
                        {{ errors.navigationKey }}
                    </b-form-invalid-feedback>
                </b-form-group>
            </b-col>

            <b-col md="6">
                <b-form-group label="Navigasyon Tipi" label-for="navigationType">
                    <v-select-list id="navigationType" v-model="localValue.navigationType" :options="navigationTypes" placeholder="Menü Tipi" label="label" :reduce="(item) => item.value" />
                </b-form-group>
            </b-col>
        </b-row>

        <b-row>
            <b-col md="6">
                <b-form-group label="Üst Navigasyon" label-for="parent">
                    <v-select-list
                        id="parent"
                        v-model="localValue.parentNavigationId"
                        :options="parentNavigations"
                        :reduce="(nav) => nav.Id"
                        label="Title"
                        placeholder="Üst Menü (Opsiyonel)"
                        clearable
                    >
                    </v-select-list>
                </b-form-group>
            </b-col>

            <b-col md="6">
                <b-form-group label="Direkt URL" label-for="customUrl">
                    <b-form-input id="customUrl" v-model="localValue.customUrl" placeholder="/sayfa-yolu veya https://external.com" />
                    <small class="text-muted">İçerik bağlı değilse manuel URL girin</small>
                </b-form-group>
            </b-col>
        </b-row>

        <b-row>
            <b-col cols="12">
                <b-form-group label="Bağlı İçerikler (Çoklu Seçim)" label-for="contents">
                    <v-select-list
                        id="contents"
                        v-model="selectedContentIds"
                        :options="contents"
                        :reduce="(c) => c.Id"
                        label="Title"
                        placeholder="İçerikler Seçin (Opsiyonel)"
                        multiple
                        clearable
                        @input="onContentSelectionChange"
                    />
                    <small class="text-muted">Birden fazla içerik seçebilirsiniz. Seçtikten sonra sıralayabilir ve ana içeriği belirleyebilirsiniz.</small>
                </b-form-group>
            </b-col>
        </b-row>

        <!-- Selected Contents List with Drag & Drop -->
        <div v-if="localValue.contentIds && localValue.contentIds.length > 0" class="bg-light rounded p-1 mb-2 border">
            <div class="d-flex align-items-center justify-content-between mb-1">
                <small class="text-muted font-weight-bold text-uppercase"> Seçili İçerikler </small>
                <b-badge variant="primary" pill>
                    {{ localValue.contentIds.length }}
                </b-badge>
            </div>

            <draggable v-model="localValue.contentIds" handle=".drag-handle" @end="updateSortOrders">
                <transition-group name="flip-list" tag="div" class="contents-list">
                    <div
                        v-for="contentItem in localValue.contentIds"
                        :key="contentItem.contentId"
                        class="content-item-card d-flex align-items-center justify-content-between bg-white border rounded shadow-sm"
                    >
                        <div class="d-flex align-items-center flex-grow-1">
                            <!-- Drag handle -->
                            <div class="pl-0 pr-1 d-flex align-items-center">
                                <feather-icon icon="MenuIcon" size="16" class="drag-handle text-muted" />
                            </div>

                            <!-- Ana içerik seçimi -->
                            <div class="cursor-pointer pl-25 pr-50 pb-25" @click="setPrimaryContent(contentItem.contentId)" v-b-tooltip.hover title="Ana İçerik Yap">
                                <feather-icon icon="StarIcon" size="18" :class="contentItem.isPrimary ? 'text-warning' : 'text-muted'" :fill="contentItem.isPrimary ? 'currentColor' : 'none'" />
                            </div>

                            <!-- Başlık -->
                            <span class="font-weight-bolder text-dark mr-2" style="font-size: 0.9rem">
                                {{ getContentTitle(contentItem.contentId) }}
                            </span>

                            <!-- Sıra bilgisi -->
                            <b-badge variant="light-secondary" class="font-weight-normal" style="font-size: 0.75rem"> Sıra: {{ contentItem.sortOrder }} </b-badge>
                        </div>

                        <div class="d-flex align-items-center mt-50">
                            <!-- Aktif / Pasif -->
                            <b-form-checkbox v-model="contentItem.isActive" switch class="mr-2" @change="validateForm">
                                <span style="font-size: 0.8rem">
                                    {{ contentItem.isActive ? "Aktif" : "Pasif" }}
                                </span>
                            </b-form-checkbox>

                            <!-- Sil -->
                            <div class="cursor-pointer text-danger pr-1 pb-75 hover-scale" @click="removeContent(contentItem.contentId)" v-b-tooltip.hover title="Listeden Çıkar">
                                <feather-icon icon="XIcon" size="18" />
                            </div>
                        </div>
                    </div>
                </transition-group>
            </draggable>
        </div>

        <b-row>
            <b-col md="6">
                <b-form-group label="Sıra" label-for="sortOrder">
                    <b-form-input id="sortOrder" v-model.number="localValue.sortOrder" type="number" min="0" />
                </b-form-group>
            </b-col>
        </b-row>

        <b-row>
            <b-col md="6">
                <b-form-group label="Durum">
                    <b-form-checkbox v-model="localValue.isActive" switch>
                        {{ localValue.isActive ? "Aktif" : "Pasif" }}
                    </b-form-checkbox>
                </b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Yeni Sekmede Aç">
                    <b-form-checkbox v-model="localValue.openInNewTab" switch>
                        {{ localValue.openInNewTab ? "Evet" : "Hayır" }}
                    </b-form-checkbox>
                </b-form-group>
            </b-col>
        </b-row>

        <b-row>
            <b-col md="6">
                <b-form-group label="Görsel Ekle">
                    <b-form-checkbox v-model="localValue.showCta" switch>
                        {{ localValue.showCta ? "Evet" : "Hayır" }}
                    </b-form-checkbox>
                </b-form-group>
            </b-col>
        </b-row>

        <b-row v-if="localValue.showCta">
            <b-col cols="12">
                <div class="mb-2 p-2 border rounded theme-item">
                    <div class="d-flex align-items-center mb-1">
                        <feather-icon icon="MousePointerIcon" size="20" class="mr-50 text-info" />
                        <h5 class="mb-0 text-info">Medya Ekle</h5>
                    </div>

                    <b-row>
                        <b-col md="6">
                            <b-form-group label="Link" label-for="ctaHref" class="mb-0">
                                <b-form-input id="ctaHref" v-model="localValue.ctaHref" placeholder="https://..." size="sm" />
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group label="ALT Metni" label-for="ctaText" class="mb-1">
                                <b-form-input id="ctaText" v-model="localValue.ctaText" placeholder="Örn: Kategori Resmi" size="sm" />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>
            </b-col>
        </b-row>

        <hr />

        <h5>
            <feather-icon icon="GlobeIcon" size="18" class="mr-50" />
            Çeviriler
        </h5>

        <b-tabs pills card class="mt-2">
            <b-tab v-for="lang in languages" :key="lang.LanguageCode" :title="lang.LanguageName">
                <navigation-translation-form :value="getTranslation(lang.LanguageCode)" :language-code="lang.LanguageCode" @input="updateTranslation(lang.LanguageCode, $event)" />
            </b-tab>
        </b-tabs>
    </div>
</template>

<script>
import { BCard, BRow, BCol, BFormGroup, BFormInput, BFormInvalidFeedback, BFormCheckbox, BFormSpinbutton, BTabs, BTab, BBadge, BButton, BAlert, VBTooltip } from "bootstrap-vue";
import vSelect from "vue-select";
import draggable from "vuedraggable";
import NavigationTranslationForm from "./NavigationTranslationForm.vue";

export default {
    name: "NavigationForm",
    components: {
        BCard,
        BRow,
        BCol,
        BFormGroup,
        BFormInput,
        BFormInvalidFeedback,
        BFormCheckbox,
        BFormSpinbutton,
        BTabs,
        BTab,
        BBadge,
        BButton,
        BAlert,
        vSelect,
        draggable,
        NavigationTranslationForm,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
        errors: {
            type: Object,
            default: () => ({}),
        },
        languages: {
            type: Array,
            default: () => [],
        },
        contents: {
            type: Array,
            default: () => [],
        },
        parentNavigations: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            navigationTypes: [
                { label: "Ana Menü", value: "main" },
                { label: "Footer Menü", value: "footer" },
                { label: "Yan Menü", value: "sidebar" },
            ],
            selectedContentIds: [],
            contentErrors: {
                duplicateContent: null,
                multiplePrimary: null,
            },
        };
    },
    computed: {
        localValue: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit("input", val);
            },
        },
    },
    created() {
        if (this.languages && this.languages.length > 0) {
            this.initializeTranslations();
        }
        this.initializeContentIds();
    },
    watch: {
        languages(newVal) {
            if (newVal && newVal.length > 0) {
                this.initializeTranslations();
            }
        },
        "localValue.contentIds": {
            deep: true,
            immediate: true,
            handler(newVal) {
                if (newVal && Array.isArray(newVal)) {
                    this.selectedContentIds = newVal.map((c) => c.contentId);
                }
            },
        },
    },
    methods: {
        initializeTranslations() {
            if (!this.localValue.translations) {
                this.$set(this.localValue, "translations", []);
            }
            this.languages.forEach((lang) => {
                if (!this.localValue.translations.find((t) => t.LanguageCode === lang.LanguageCode)) {
                    this.localValue.translations.push({
                        LanguageCode: lang.LanguageCode,
                        Title: "",
                        Description: "",
                        CtaText: "",
                        CtaHref: "",
                    });
                }
            });
        },

        getTranslation(languageCode) {
            let translation = this.localValue.translations?.find((t) => t.LanguageCode === languageCode);
            if (!translation) {
                translation = {
                    LanguageCode: languageCode,
                    Title: "",
                    Description: "",
                    CtaText: "",
                    CtaHref: "",
                };
            }
            return translation;
        },

        updateTranslation(languageCode, data) {
            const index = this.localValue.translations.findIndex((t) => t.LanguageCode === languageCode);
            if (index !== -1) {
                this.$set(this.localValue.translations, index, data);
            } else {
                this.localValue.translations.push(data);
            }
        },

        getValidationState(field) {
            return this.errors[field] ? false : null;
        },

        initializeContentIds() {
            if (!this.localValue.contentIds) {
                this.$set(this.localValue, "contentIds", []);
            }
        },

        // Content Management Methods
        onContentSelectionChange(selectedIds) {
            if (!this.localValue.contentIds) {
                this.$set(this.localValue, "contentIds", []);
            }

            // Add new selections
            selectedIds.forEach((contentId) => {
                if (!this.localValue.contentIds.find((c) => c.contentId === contentId)) {
                    this.localValue.contentIds.push({
                        contentId: contentId,
                        isPrimary: this.localValue.contentIds.length === 0, // First one is primary by default
                        sortOrder: this.localValue.contentIds.length,
                        isActive: true,
                    });
                }
            });

            // Remove deselected items
            this.localValue.contentIds = this.localValue.contentIds.filter((c) => selectedIds.includes(c.contentId));

            this.updateSortOrders();
            this.validateForm();
        },

        getContentTitle(contentId) {
            const content = this.contents.find((c) => c.Id === contentId);
            return content ? content.Title : "Bilinmeyen İçerik";
        },

        setPrimaryContent(contentId) {
            // Remove primary from all
            this.localValue.contentIds.forEach((c) => {
                c.isPrimary = false;
            });

            // Set new primary
            const target = this.localValue.contentIds.find((c) => c.contentId === contentId);
            if (target) {
                target.isPrimary = true;
            }

            this.validateForm();
        },

        removeContent(contentId) {
            this.localValue.contentIds = this.localValue.contentIds.filter((c) => c.contentId !== contentId);
            this.selectedContentIds = this.selectedContentIds.filter((id) => id !== contentId);
            this.updateSortOrders();
            this.validateForm();
        },

        updateSortOrders() {
            if (this.localValue.contentIds && this.localValue.contentIds.length > 0) {
                this.localValue.contentIds.forEach((content, index) => {
                    content.sortOrder = index;
                });
            }
        },

        validateForm() {
            this.contentErrors = {
                duplicateContent: null,
                multiplePrimary: null,
            };

            if (!this.localValue.contentIds || this.localValue.contentIds.length === 0) {
                return true;
            }

            // Check for duplicates
            const contentIdSet = new Set();
            for (const item of this.localValue.contentIds) {
                if (contentIdSet.has(item.contentId)) {
                    this.contentErrors.duplicateContent = "Aynı içerik birden fazla kez eklenemez!";
                    return false;
                }
                contentIdSet.add(item.contentId);
            }

            // Check for multiple primary contents
            const primaryCount = this.localValue.contentIds.filter((c) => c.isPrimary).length;
            if (primaryCount > 1) {
                this.contentErrors.multiplePrimary = "Bir navigasyonda yalnızca bir ana içerik olabilir!";
                return false;
            }

            return true;
        },
    },
};
</script>

<style scoped>
.is-required >>> label::after,
.is-required /deep/ label::after {
    content: " *";
    color: #dc3545;
    font-weight: 700;
}

.selected-contents-container {
    background: #f8f9fa;
    padding: 16px;
    border-radius: 8px;
    margin-top: 16px;
}

.selected-contents-container h6 {
    font-weight: 600;
    color: #495057;
    display: flex;
    align-items: center;
}
.contents-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.content-item-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    transition: all 0.2s ease;
}
.content-item-card:hover {
    border-color: #d1d5db;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
.content-item-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.drag-handle {
    color: #9ca3af;
    cursor: grab;
    flex-shrink: 0;
}
.drag-handle:active {
    cursor: grabbing;
}
.drag-handle:hover {
    color: #6b7280;
}
.content-title {
    font-weight: 500;
    color: #1f2937;
    font-size: 14px;
}

.content-item-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.flip-list-move {
    transition: transform 0.3s;
}

::v-deep .btn-icon {
    width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
